import { useEffect, useState } from 'react'

import { LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT } from '../services/liveKitVideoBackground'

export interface CameraPermissionBlockedState {
  /** Access looks blocked, by either signal. Drives the words on the button. */
  blocked: boolean
  /**
   * The Permissions API itself says "denied". The only signal sure enough to
   * disable turning the camera on.
   */
  confirmed: boolean
}

/**
 * Whether the camera cannot be started because access is blocked.
 *
 * Two independent signals, because neither alone is sufficient:
 *  - **The Permissions API**, queried up front and watched for changes. It knows
 *    the state before the user presses anything, but it is Chromium-only for
 *    `'camera'`; Firefox rejects the query outright and Safari's support is
 *    partial.
 *  - **An actual failed camera start**, reported by the room connector. This is
 *    the fallback that covers the browsers the query does not, and it is also
 *    what clears the state once a start finally succeeds.
 *
 * Only a "denied" answer is `confirmed`. A failed start alone can go stale
 * without any event telling us: Firefox has no query, an OS-level denial (macOS
 * System Settings) leaves the site permission "granted", and a dismissed prompt
 * stays "prompt". So a reported failure names the obstacle but leaves the button
 * clickable: the click retries, and either reports the failure again or clears
 * it by starting the camera.
 *
 * Anything unknown resolves to **not blocked**: disabling a control on a guess
 * would strand users whose browser simply does not implement the query.
 */
export function useCameraPermissionBlocked(): CameraPermissionBlockedState {
  const [reported, setReported] = useState(false)
  const [confirmed, setConfirmed] = useState(false)

  useEffect(() => {
    const handleReported = (event: CustomEvent<{ blocked: boolean }>) => {
      const blocked = Boolean(event.detail?.blocked)
      setReported(blocked)
      // A camera that started is not denied, whatever the last answer said.
      if (!blocked) setConfirmed(false)
    }
    window.addEventListener(
      LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT,
      handleReported as EventListener
    )

    let status: PermissionStatus | null = null
    let cancelled = false
    const readStatus = () => {
      if (status) setConfirmed(status.state === 'denied')
    }
    // The user changed the site permission: newer than any failed start.
    const handleStatusChange = () => {
      setReported(false)
      readStatus()
    }

    void (async () => {
      try {
        const permissions = navigator.permissions
        if (!permissions?.query) return
        const result = await permissions.query({ name: 'camera' as PermissionName })
        if (cancelled) return
        status = result
        readStatus()
        result.addEventListener?.('change', handleStatusChange)
      } catch {
        // Firefox rejects a 'camera' query and older browsers lack the API
        // entirely. Stay on the failed-start signal rather than guessing.
      }
    })()

    return () => {
      cancelled = true
      status?.removeEventListener?.('change', handleStatusChange)
      window.removeEventListener(
        LIVEKIT_CAMERA_PERMISSION_BLOCKED_EVENT,
        handleReported as EventListener
      )
    }
  }, [])

  return { blocked: confirmed || reported, confirmed }
}

export default useCameraPermissionBlocked
